'use client';

import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { Icon, type IconName } from '@/components/ui/icon';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  exiting?: boolean;
}

interface ToastContextValue {
  toasts: Toast[];
  toast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue>({
  toasts: [],
  toast: () => {},
});

const TOAST_DURATION = 3000;
const FADE_OUT_DURATION = 300;

// Habillage de la maquette : bandeau sombre en bas d'écran, icône colorée à gauche.
const TOAST_STYLE: Record<ToastType, { icon: IconName; color: string }> = {
  success: { icon: 'check', color: '#8fe0ab' },
  error: { icon: 'alert', color: '#ffb0a6' },
  info: { icon: 'bell', color: '#e7c574' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextIdRef = useRef(0);

  const addToast = useCallback((message: string, type: ToastType = 'success') => {
    const id = nextIdRef.current++;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
    }, TOAST_DURATION);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, TOAST_DURATION + FADE_OUT_DURATION);
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, toast: addToast }}>
      {children}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="pointer-events-none fixed bottom-7 left-1/2 z-60 flex -translate-x-1/2 flex-col items-center gap-2 px-4"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto flex animate-tmtoast items-center gap-2.5 rounded-[13px] bg-navy-d px-[22px] py-[13px] text-[14.5px] font-medium text-white shadow-toast transition-opacity ${
              t.exiting ? 'opacity-0' : 'opacity-100'
            }`}
            style={{ maxWidth: '90vw' }}
          >
            <span className="flex" style={{ color: TOAST_STYLE[t.type].color }}>
              <Icon name={TOAST_STYLE[t.type].icon} size={18} />
            </span>
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  return useContext(ToastContext);
}
